import { Guide } from '../../types/guide';

const guide: Guide = {
  slug: 'getting-started-every-tool',
  title: 'Getting Started: Every Tool on This Site in 5 Minutes',
  description:
    'A quick tour of every free tool here — document converter, PDF tools, online text editor, PDF export and typing test — and the best tip for each.',
  category: 'Getting started',
  publishedDate: '2026-11-03',
  affiliateCategory: 'general',
  body: `
Everything on this site runs **in your browser**. Files you open and text you type are never uploaded to a server, there's no account, and nothing is stored anywhere except your own browser. Here's what each tool does and the one tip worth knowing for each.

## 1. Converter — PDF, Word & Excel to text or Markdown

Drop \`.pdf\`, \`.docx\` or \`.xlsx\` files onto the [Converter](/) and you get plain text or Markdown you can copy or download. Select several files to get one combined output.

**Best tip:** switch the output to **Markdown** when you want headings, lists and tables to survive — perfect for pasting into notes apps, wikis or AI chat tools. → [Converter tips & tricks](/guides/pdf-word-excel-converter-tips)

## 2. PDF tools — merge, split, reorder, flatten, export images

Below the converter output you'll find tools that work on the PDFs themselves: merge selected PDFs, extract page ranges, reorder pages, flatten layered PDFs and export pages as PNG or JPEG.

**Best tip:** page ranges accept lists like \`1-3, 5, 8-10\`. → [Split, reorder, flatten & export pages](/guides/split-reorder-flatten-export-pdf-pages)

## 3. Text Editor — clean up, transform and convert text

The [Text Editor](/text-editor) is a full editor with find & replace (including regex), syntax highlighting, ~50 one-click text tools and conversion between plain text, Markdown, HTML, CSV, TSV, JSON, XML and YAML.

**Best tip:** after pasting text copied from a PDF, click **Fix PDF line breaks** — it re-joins wrapped lines into proper paragraphs. → [Clean up text copied from a PDF](/guides/clean-up-text-copied-from-pdf)

## 4. Export to PDF — from plain text or Markdown

From the editor's **Download** menu choose **PDF document** to create a real PDF in your browser, with headings, lists, coloured code, clickable links and a bookmark sidebar.

**Best tip:** write in Markdown, then export — you get a formatted document without touching a word processor. → [Export text & Markdown to PDF](/guides/export-text-and-markdown-to-pdf)

## 5. Typing Test — measure and improve your speed

The [Typing Test](/typing-test) measures words per minute and accuracy with timed tests, quotes, real paragraphs or your own text, and includes guided exercises with an on-screen finger guide and optional keyboard sounds.

**Best tip:** after a test, click **Practice these keys** to drill exactly the letters you miss most. → [How to improve your typing speed](/guides/how-to-improve-typing-speed)

## Handy things that work everywhere

- **Dark mode:** the sun/moon button in the top-right corner. Your choice is remembered.
- **Pick up where you left off:** the site reopens the tool you used last, and the editor autosaves your draft in this browser.
- **Send converter output to the editor:** click the **Open in Text Editor** button above any converted text to keep working on it.
- **Private by design:** conversion, editing, PDF export and typing stats all run on your device. Nothing you open or type is sent to a server.
`,
};

export default guide;
